/**
 * SiteMultiPicker — checkbox-style picker for selecting which sites are
 * included in the comparison view. Allows 1–N (the container enforces
 * the ≥ 2 minimum and surfaces a warning); recommends ≤ 5 for table
 * readability.
 *
 * The picker is presentational: parent owns the selectedIds set and the
 * onToggle / onSelectAll / onClear handlers. Selection state lives in
 * the URL (`?sites=a,b,c`) so the picker is round-trippable.
 *
 * Linear: MYD-216 (F1).
 */

import { Card } from 'components/ui/card'
import { Button } from 'components/ui/button'
import { Checkbox } from 'components/ui/checkbox'
import { Building2, AlertCircle } from 'lucide-react'
import { cn } from 'lib/utils'

interface SiteMultiPickerProps {
	sites: any[]
	selectedIds: string[]
	onToggle: (siteId: string) => void
	onSelectAll: () => void
	onClear: () => void
	rankingsBySiteId?: Record<string, number>
	maxRecommended?: number
}

const SiteMultiPicker = ({
	sites,
	selectedIds,
	onToggle,
	onSelectAll,
	onClear,
	rankingsBySiteId,
	maxRecommended = 5,
}: SiteMultiPickerProps) => {
	const selectedSet = new Set(selectedIds)
	const tooFew = selectedIds.length < 2
	const tooMany = selectedIds.length > maxRecommended

	return (
		<Card className="p-4">
			<div className="flex items-center justify-between gap-3 mb-3">
				<div>
					<h4 className="text-sm font-semibold text-gray-900">Sites to compare</h4>
					<p className="text-xs text-gray-500 mt-0.5">
						Pick 2 or more (recommended ≤ {maxRecommended} for readability).
					</p>
				</div>
				<div className="flex items-center gap-1.5">
					<Button variant="ghost" onClick={onSelectAll} className="h-7 px-2 text-xs">
						All
					</Button>
					<Button variant="ghost" onClick={onClear} className="h-7 px-2 text-xs">
						None
					</Button>
				</div>
			</div>

			{sites.length === 0 ? (
				<div className="text-center py-6">
					<Building2 className="mx-auto h-8 w-8 text-gray-300 mb-2" />
					<p className="text-xs text-gray-500">
						No sites yet. Add sites in the Sites tab first.
					</p>
				</div>
			) : (
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
					{sites.map((site) => {
						const checked = selectedSet.has(site._id)
						const rank = rankingsBySiteId?.[site._id]
						return (
							<label
								key={site._id}
								className={cn(
									'flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors',
									checked
										? 'border-[#0080fe] bg-blue-50/40'
										: 'border-gray-200 hover:border-gray-300',
								)}
							>
								<Checkbox checked={checked} onCheckedChange={() => onToggle(site._id)} />
								<span className="text-sm font-medium text-gray-800 truncate flex-1">
									{site.name}
								</span>
								{rank != null && (
									<span className="text-[10px] uppercase tracking-wide text-gray-500 shrink-0">
										#{rank}
									</span>
								)}
							</label>
						)
					})}
				</div>
			)}

			{(tooFew || tooMany) && (
				<div
					className={cn(
						'mt-3 flex items-start gap-2 px-3 py-2 rounded-md text-xs border',
						tooFew
							? 'bg-amber-50 border-amber-200 text-amber-700'
							: 'bg-blue-50 border-blue-200 text-blue-700',
					)}
				>
					<AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
					<span>
						{tooFew
							? 'Select at least 2 sites to enable comparison.'
							: `Comparing ${selectedIds.length} sites — readability drops past ${maxRecommended}.`}
					</span>
				</div>
			)}
		</Card>
	)
}

export default SiteMultiPicker
